import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import AddressAutocomplete from '../components/AddressAutocomplete'

export default function Settings() {
  const { profile, updateProfile } = useAuth()
  const [address, setAddress] = useState(profile?.address || '')
  const [coordinates, setCoordinates] = useState(
    profile?.lat && profile?.lng ? { lat: profile.lat, lng: profile.lng } : null
  )
  const [radius, setRadius] = useState(profile?.radius_miles || 1)
  const [transportMode, setTransportMode] = useState(profile?.transport_mode || 'walking')
  const [frequency, setFrequency] = useState(profile?.notification_frequency || 'immediate')
  const [notificationsEnabled, setNotificationsEnabled] = useState(profile?.notifications_enabled ?? true)
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [error, setError] = useState(null)

  const handleAddressSelect = (result) => {
    setAddress(result.formatted)
    setCoordinates({ lat: result.lat, lng: result.lng })
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    console.log('Settings: handleSubmit started')
    setLoading(true)
    setError(null)
    setMessage(null)

    if (!coordinates) {
      setError('Please select an address from the suggestions')
      setLoading(false)
      return
    }

    console.log('Settings: calling updateProfile with:', {
      address,
      lat: coordinates.lat,
      lng: coordinates.lng,
      radius_miles: parseFloat(radius),
      transport_mode: transportMode,
      notification_frequency: frequency,
      notifications_enabled: notificationsEnabled
    })

    const { data: updatedProfile, error: updateError } = await updateProfile({
      address,
      lat: coordinates.lat,
      lng: coordinates.lng,
      radius_miles: parseFloat(radius),
      transport_mode: transportMode,
      notification_frequency: frequency,
      notifications_enabled: notificationsEnabled
    })

    console.log('Settings: updateProfile returned:', { updatedProfile, updateError })

    if (updateError) {
      setError(updateError.message)
    } else {
      setMessage('Settings saved successfully!')

      // Send nearby vendors email
      if (updatedProfile) {
        try {
          await fetch('/.netlify/functions/send-welcome-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: updatedProfile.id,
              email: updatedProfile.email,
              lat: coordinates.lat,
              lng: coordinates.lng,
              radius_miles: parseFloat(radius),
              transport_mode: transportMode
            })
          })
        } catch (emailError) {
          console.error('Failed to send email:', emailError)
        }
      }
    }

    setLoading(false)
  }

  return (
    <div className="page page-narrow">
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your location and notification preferences</p>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <form onSubmit={handleSubmit}>
        {/* Location Settings */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="settings-section">
            <h3 className="settings-section-title">Location</h3>

            <div className="form-group">
              <label htmlFor="address" className="form-label">Your Address</label>
              <AddressAutocomplete
                value={address}
                onChange={(val) => {
                  setAddress(val)
                  if (val !== address) setCoordinates(null)
                }}
                onSelect={handleAddressSelect}
                placeholder="Start typing your address..."
              />
              <p className="form-helper">
                Select your address from the suggestions
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">
                Search Radius: <strong>{radius} mile{radius !== 1 ? 's' : ''}</strong>
              </label>
              <input
                type="range"
                min="0.25"
                max="5"
                step="0.25"
                value={radius}
                onChange={(e) => setRadius(parseFloat(e.target.value))}
                style={{
                  width: '100%',
                  height: '8px',
                  borderRadius: '4px',
                  background: 'var(--border)',
                  appearance: 'none',
                  cursor: 'pointer'
                }}
              />
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: 'var(--text-light)',
                marginTop: '4px'
              }}>
                <span>0.25 mi</span>
                <span>5 mi</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="transport" className="form-label">Transport Mode</label>
              <select
                id="transport"
                className="form-select"
                value={transportMode}
                onChange={(e) => setTransportMode(e.target.value)}
              >
                <option value="walking">Walking</option>
                <option value="driving">Driving</option>
              </select>
              <p className="form-helper">
                Distance will be calculated based on actual {transportMode} routes
              </p>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="settings-section">
            <h3 className="settings-section-title">Notifications</h3>

            <div className="form-group">
              <div className="toggle">
                <input
                  type="checkbox"
                  className="toggle-input"
                  id="notifications-enabled"
                  checked={notificationsEnabled}
                  onChange={(e) => setNotificationsEnabled(e.target.checked)}
                />
                <label htmlFor="notifications-enabled" className="toggle-label">
                  Enable email notifications
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="frequency" className="form-label">Notification Frequency</label>
              <select
                id="frequency"
                className="form-select"
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                disabled={!notificationsEnabled}
              >
                <option value="immediate">Immediate</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Digest</option>
              </select>
              <p className="form-helper">
                {frequency === 'immediate' && 'Get notified as soon as changes are detected (hourly sync)'}
                {frequency === 'daily' && 'Receive a summary email once per day'}
                {frequency === 'weekly' && 'Receive a summary email once per week'}
              </p>
            </div>

            <div style={{
              background: 'var(--background)',
              padding: '16px',
              borderRadius: 'var(--radius)',
              marginTop: '16px'
            }}>
              <p style={{ fontWeight: '600', marginBottom: '8px', fontSize: '14px' }}>
                You'll be notified when:
              </p>
              <ul style={{
                paddingLeft: '20px',
                fontSize: '13px',
                color: 'var(--text-secondary)',
                lineHeight: '1.8'
              }}>
                <li>A brand new vendor is added near you</li>
                <li>An existing vendor adds a new location near you</li>
                <li>A vendor changes their schedule at a location near you</li>
              </ul>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-lg"
          disabled={loading}
          style={{ width: '100%' }}
        >
          {loading ? 'Saving...' : 'Save Settings'}
        </button>
      </form>

      {profile?.lat && profile?.lng && (
        <div style={{
          marginTop: '24px',
          padding: '16px',
          background: 'var(--background)',
          borderRadius: 'var(--radius)',
          fontSize: '13px',
          color: 'var(--text-secondary)'
        }}>
          <strong>Current coordinates:</strong> {profile.lat.toFixed(6)}, {profile.lng.toFixed(6)}
        </div>
      )}
    </div>
  )
}
